/**
 * China Delight store schedule.
 *
 * Single source of truth for whether we're open and able to take online
 * orders. Edit this file (and redeploy) to change hours.
 *
 * Three layers, applied in priority order:
 *
 *   1. `manual.closed` — the kill switch. If `true`, we're closed for any
 *      reason (kitchen ran out of food, equipment broken, owner's call).
 *      Set `manual.reason` to whatever message customers should see.
 *
 *   2. `overrides[YYYY-MM-DD]` — single-day overrides for holidays or
 *      special days. Two shapes:
 *        null                       → closed all day
 *        { open: "11:00", close: "21:30" }  → custom hours
 *      Use this to OPEN on a normally-closed Tuesday (e.g., Valentine's)
 *      OR to CLOSE on a normally-open day (e.g., Thanksgiving).
 *
 *   3. `weekly[dayName]` — the regular weekly schedule. `null` = closed.
 *
 * Times are in 24-hour "HH:MM" format and represent local Forest Hill
 * (Eastern) time — no timezone math is performed. Editor: trust your
 * local clock.
 */

const DAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

const STORE_SCHEDULE = {
  weekly: {
    sunday:    { open: "12:00", close: "21:30" },
    monday:    { open: "10:30", close: "21:30" },
    tuesday:   null,                              // CLOSED Tuesdays
    wednesday: { open: "10:30", close: "21:30" },
    thursday:  { open: "10:30", close: "21:30" },
    friday:    { open: "10:30", close: "21:30" },
    saturday:  { open: "10:30", close: "21:30" },
  },

  // YYYY-MM-DD → null (closed all day) OR { open, close } (custom hours)
  // Use to override the weekly schedule for a single date.
  overrides: {
    // Examples — uncomment / edit as needed:
    // "2026-02-14": { open: "11:00", close: "21:30" },  // Valentine's: open Tuesday
    // "2026-11-26": null,                                // Thanksgiving: closed
    // "2026-12-25": null,                                // Christmas Day: closed
    // "2026-12-31": { open: "11:00", close: "20:00" },   // NYE: early close
  },

  // Manual kill switch — edit this and redeploy to flip the site to closed.
  // `reason` is shown to customers in the banner / cart message.
  manual: {
    closed: false,
    reason: "",
  },
};

// ---- pure helpers (no React) ---------------------------------------

/**
 * Format a Date as "YYYY-MM-DD" in the user's local time.
 */
function dateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Convert "HH:MM" → minutes since midnight.
 */
function minutesOf(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

/**
 * Resolve the open window for the given Date (local time):
 *   { open, close }   → store is open this day during [open, close)
 *   null              → store is closed all day
 *
 * Resolution order: manual.closed > overrides[date] > weekly[day].
 * Returns `{ kind: "manual"|"override"|"weekly", window: ... | null, reason? }`
 */
export function resolveSchedule(date = new Date(), schedule = STORE_SCHEDULE) {
  if (schedule.manual?.closed) {
    return {
      kind: "manual",
      window: null,
      reason: schedule.manual.reason || "We're temporarily closed.",
    };
  }
  const key = dateKey(date);
  if (Object.prototype.hasOwnProperty.call(schedule.overrides || {}, key)) {
    return {kind: "override", window: schedule.overrides[key]};
  }
  const dayName = DAYS[date.getDay()];
  return {kind: "weekly", window: schedule.weekly[dayName] || null};
}

/**
 * Is the store open at the moment `at`? Returns:
 *   { isOpen: true,  closesAt: "21:30" }
 *   { isOpen: false, reason: "...", nextOpen: Date | null }
 */
export function getStoreStatus(at = new Date(), schedule = STORE_SCHEDULE) {
  const today = resolveSchedule(at, schedule);
  if (today.kind === "manual") {
    return {isOpen: false, reason: today.reason, nextOpen: findNextOpen(at, schedule)};
  }
  if (!today.window) {
    // closed all day (override or weekly null)
    return {
      isOpen: false,
      reason: "We're closed today.",
      nextOpen: findNextOpen(at, schedule),
    };
  }
  const now = at.getHours() * 60 + at.getMinutes();
  const open = minutesOf(today.window.open);
  const close = minutesOf(today.window.close);
  if (now < open) {
    return {
      isOpen: false,
      reason: `We open at ${formatTime(today.window.open)}.`,
      nextOpen: setTime(at, today.window.open),
    };
  }
  if (now >= close) {
    return {
      isOpen: false,
      reason: `We closed at ${formatTime(today.window.close)}.`,
      nextOpen: findNextOpen(at, schedule),
    };
  }
  return {isOpen: true, closesAt: today.window.close};
}

/**
 * Will the store be open at a *future* moment (e.g., the customer's
 * scheduled pickup time)? Same return shape as getStoreStatus.
 */
export function isOpenAt(pickupDate, schedule = STORE_SCHEDULE) {
  return getStoreStatus(pickupDate, schedule);
}

/**
 * Walk forward up to 7 days to find the next time the store opens.
 * Returns a Date set to that opening time, or null if nothing is found.
 */
function findNextOpen(from, schedule) {
  const probe = new Date(from);
  for (let i = 0; i < 8; i++) {
    const day = resolveSchedule(probe, schedule);
    if (day.kind !== "manual" && day.window) {
      const candidate = setTime(probe, day.window.open);
      if (candidate > from) return candidate;
    }
    // advance to the next calendar day at 00:00
    probe.setDate(probe.getDate() + 1);
    probe.setHours(0, 0, 0, 0);
  }
  return null;
}

function setTime(baseDate, hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date(baseDate);
  d.setHours(h, m, 0, 0);
  return d;
}

/**
 * "21:30" → "9:30 PM"
 */
export function formatTime(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ap}`;
}

export default STORE_SCHEDULE;
